import { ChevronDown, Dice6, Shield, Sparkles, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { FAQ_CATEGORIES, PRIVACY_ITEMS, type FAQItem } from './about/faq-data';

/**
 * Collapsible FAQ item component.
 */
function FAQAccordionItem({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-4 text-left text-sm font-medium transition-colors hover:text-primary"
      >
        {item.question}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          isOpen ? 'max-h-40 pb-4' : 'max-h-0'
        )}
      >
        <p className="text-sm text-muted-foreground">{item.answer}</p>
      </div>
    </div>
  );
}

/**
 * About/Help page with credits, user guide, and FAQ.
 * Uses card-based layout with max-w-card for better scaling on larger screens.
 */
export function AboutPage() {
  /**
   * Check the service worker for an available update.
   * Shows a success toast if an update is found, or an info toast if already up to date.
   */
  async function checkForUpdate() {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      if (registration) {
        await registration.update();
        if (registration.waiting) {
          toast.success('Update found! Installing...');
          return;
        }
      }
      toast.info(`You're up to date — v${__APP_VERSION__} (${__GIT_HASH__})`);
    } catch {
      toast.info(`You're up to date — v${__APP_VERSION__} (${__GIT_HASH__})`);
    }
  }

  return (
    <div className="flex h-full flex-col p-4 overflow-y-auto">
      <div className="w-full max-w-card mx-auto min-h-0">
        <PageHeader
          variant="lost"
          title="Help"
          trailing={
            <Badge
              variant="accent"
              role="button"
              tabIndex={0}
              className="cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity"
              onClick={checkForUpdate}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') checkForUpdate(); }}
            >
              v{__APP_VERSION__}
            </Badge>
          }
        />

        <div className="flex-1 space-y-6">
          {/* Credits Section */}
          <Card variant="elevated">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                About Dice Frog
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm md:text-base text-muted-foreground mb-3">
                Capture physical dice rolls and send them to your game session on any supported platform.
              </p>
              <p className="text-sm md:text-base">
                Developed by <span className="text-primary">James van der Walt</span> and contributors
              </p>
            </CardContent>
          </Card>

          {/* User Guide Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-glow-subtle">User Guide</h2>

            {/* For Players */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  For Players
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm md:text-base text-muted-foreground">
                  <li>Open the app on your phone</li>
                  <li>Scan the QR code on the GM&apos;s screen or in the Discord channel to pre-fill the session code, or enter the code manually</li>
                  <li>Enter your player name and tap Go</li>
                  <li>Tap Connect on your session card, then use the Roll page to capture dice</li>
                </ol>
              </CardContent>
            </Card>

            {/* For GMs */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  For Game Masters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm md:text-base text-muted-foreground">
                  <li>Enter a session code and your name on the home screen, then tap Go</li>
                  <li>Share the QR code on your screen for players to scan, or give them the room code to enter manually</li>
                  <li>Tap Connect on your session card to start the session</li>
                  <li>Optionally, connect your VTT platform (Foundry module) or Discord bot using the same room code via /frog-link</li>
                  <li>Wait for players to join, then request rolls from connected players</li>
                </ol>
              </CardContent>
            </Card>

            {/* For Dice Registration */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Dice6 className="h-4 w-4 text-primary" />
                  Registering Custom Dice
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm md:text-base text-muted-foreground">
                  <li>Go to Settings and tap &quot;Register Dice&quot;</li>
                  <li>Select the dice type you want to register</li>
                  <li>Photograph each face when prompted (good lighting helps)</li>
                  <li>Review your captures and retake any blurry images</li>
                  <li>Submit for training (takes 2-4 hours)</li>
                  <li>Your trained model appears on the Register page when ready</li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-glow-subtle">Frequently Asked Questions</h2>
            {FAQ_CATEGORIES.map((category) => (
              <Collapsible key={category.title} title={category.title} defaultOpen={false}>
                {category.items.map((item, index) => (
                  <FAQAccordionItem key={index} item={item} />
                ))}
              </Collapsible>
            ))}
          </div>

          {/* Privacy & Data Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-glow-subtle">Privacy &amp; Data</h2>
            <Card>
              <CardContent className="pt-4">
                {PRIVACY_ITEMS.map((item, index) => (
                  <FAQAccordionItem key={index} item={item} />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Terms of Use Section */}
          <Card variant="elevated">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Terms of Use
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Dice Frog is provided &quot;as is&quot; without warranties of any kind. While we strive for accurate dice recognition, results are not guaranteed and should not be relied upon for high-stakes decisions.
                </p>
                <p>
                  You are responsible for the player name you choose. Offensive or inappropriate names may result in session termination.
                </p>
                <p>
                  We reserve the right to modify, suspend, or discontinue the service at any time without prior notice. We are not liable for any loss or inconvenience resulting from service changes or interruptions.
                </p>
                <p>
                  By using Dice Frog, you agree to these terms. If you do not agree, please discontinue use of the service.
                </p>
                <p className="text-xs text-muted-foreground/70 pt-2">
                  Last updated: March 2026
                </p>
              </div>
            </CardContent>
          </Card>

        </div>

        <footer className="py-4 text-center text-sm text-muted-foreground">
          <div className="h-px w-24 mx-auto mb-4 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        </footer>
      </div>
    </div>
  );
}
